"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { api, type EventoDto, type EventoEsternoDto } from "@/lib/api";
import { resolveImageUrl, resolveMediaUrl } from "@/lib/media-url";

interface EventoPromoBlockProps {
  kind: "evento" | "esterno";
  slug: string;
  heading?: string;
  className?: string;
}

interface Promo {
  title: string;
  href: string;
  external: boolean;
  poster: string | null;
  dataLabel: string;
  ora: string | null;
  luogo: string | null;
  segnalato: boolean;
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function fromEvento(e: EventoDto): Promo {
  return {
    title: e.titolo,
    href: `/eventi/${e.slug}`,
    external: false,
    poster: resolveImageUrl(e.foto) || resolveImageUrl(e.volantino),
    dataLabel: fmt(e.data),
    ora: e.oraInizio ?? null,
    luogo: e.luogo ?? null,
    segnalato: false,
  };
}

function fromEsterno(e: EventoEsternoDto): Promo {
  const id = e.fotoMediaId ?? e.volantinoMediaId;
  return {
    title: e.title,
    // urlEsterna (linkUrl) vince; altrimenti la nostra pagina di dettaglio.
    href: e.linkUrl || `/eventi-segnalati/${e.slug}`,
    external: !!e.linkUrl,
    poster: id ? resolveMediaUrl({ id } as unknown as Parameters<typeof resolveMediaUrl>[0]) : null,
    dataLabel: fmt(e.data),
    ora: e.oraInizio ?? null,
    luogo: e.location ?? null,
    segnalato: true,
  };
}

export function EventoPromoBlock({ kind, slug, heading, className }: EventoPromoBlockProps) {
  const [promo, setPromo] = useState<Promo | null | "loading" | "error">("loading");

  useEffect(() => {
    if (!slug) { setPromo("error"); return; }
    let cancelled = false;
    const p =
      kind === "esterno"
        ? api.eventiEsterni.bySlug(slug).then(fromEsterno)
        : api.eventi.bySlug(slug).then(fromEvento);
    p.then((r) => { if (!cancelled) setPromo(r); })
      .catch(() => { if (!cancelled) setPromo("error"); });
    return () => { cancelled = true; };
  }, [kind, slug]);

  if (promo === "loading") {
    return <div className={`max-w-2xl mx-auto h-28 rounded-xl bg-neutral-100 animate-pulse my-6 ${className || ""}`} />;
  }
  if (promo === "error" || promo === null) return null;

  const inner = (
    <div className="flex gap-4 p-4 items-start">
      {promo.poster && (
        <div className="shrink-0 w-20 sm:w-24 rounded-lg overflow-hidden border border-neutral-100 bg-neutral-50">
          <Image src={promo.poster} alt={promo.title} width={240} height={330} sizes="96px" className="w-full h-auto" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div
          className={`inline-block text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mb-1.5 ${
            promo.segnalato ? "bg-amber-600" : "bg-[#1a3a5c]"
          }`}
        >
          {promo.segnalato ? "Segnalato" : "Evento"}
        </div>
        <h3 className="font-bold text-base sm:text-lg text-[#1a3a5c] leading-snug">{promo.title}</h3>
        <p className="mt-1 text-sm font-semibold text-neutral-700">
          {promo.dataLabel}
          {promo.ora && <> &middot; ore {promo.ora.slice(0, 5)}</>}
        </p>
        {promo.luogo && <p className="text-sm text-neutral-500">{promo.luogo}</p>}
        <p className="mt-1.5 text-[11px] font-semibold text-[#4a90c4]">
          {promo.external ? "↗ Vai all'evento" : "Scopri di più →"}
        </p>
      </div>
    </div>
  );

  return (
    <div className={`max-w-2xl mx-auto my-6 ${className || ""}`}>
      {heading && (
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">{heading}</p>
      )}
      <article className="bg-white rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
        {promo.external ? (
          <a href={promo.href} target="_blank" rel="noopener noreferrer" className="block">{inner}</a>
        ) : (
          <a href={promo.href} className="block">{inner}</a>
        )}
      </article>
    </div>
  );
}
